import { useEffect, useMemo, useState } from "react";
import firebase from "firebase/app";
import "firebase/firestore";

import { db } from "../../firebase/firebase.utils";

const safeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\./g, "_");

const pruneTimes = new Map();
const PRUNE_COOLDOWN = 25000;

async function prunePostSignals(uid) {
  const now = Date.now();
  if (now - (pruneTimes.get(uid) || 0) < PRUNE_COOLDOWN) return;
  pruneTimes.set(uid, now);

  const userRef = db.collection("users").doc(uid);
  const snapshot = await userRef.get();
  if (!snapshot.exists) return;

  const signals = snapshot.data()?.postSignals || {};
  const updates = {};
  const remove = firebase.firestore.FieldValue.delete();

  const keepLargest = (values, limit, path) => {
    const entries = Object.entries(values || {});
    if (entries.length <= limit) return;

    entries.sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
    entries.slice(limit).forEach(([entryKey]) => {
      updates[`${path}.${entryKey}`] = remove;
    });
  };

  keepLargest(signals.topicScore, 30, "postSignals.topicScore");
  keepLargest(signals.tagScore, 60, "postSignals.tagScore");
  keepLargest(signals.authorScore, 60, "postSignals.authorScore");
  keepLargest(signals.seen, 120, "postSignals.seen");

  if (Object.keys(updates).length) await userRef.update(updates);
}

export function usePostReactions(postId, uid, enabled = true) {
  const [reactions, setReactions] = useState({});

  useEffect(() => {
    if (!enabled || !postId) {
      setReactions({});
      return undefined;
    }

    return db
      .collection("posts")
      .doc(postId)
      .collection("reactions")
      .onSnapshot(
        (snapshot) => {
          const next = {};
          snapshot.forEach((item) => {
            const data = item.data();
            if (data?.emoji) next[item.id] = data;
          });
          setReactions(next);
        },
        (error) => console.log("Post reactions snapshot error:", error),
      );
  }, [postId, enabled]);

  const groups = useMemo(() => {
    const totals = {};
    Object.values(reactions).forEach(({ emoji }) => {
      if (emoji) totals[emoji] = (totals[emoji] || 0) + 1;
    });

    return Object.entries(totals)
      .map(([emoji, count]) => ({ emoji, count }))
      .sort((a, b) => b.count - a.count);
  }, [reactions]);

  return {
    groups,
    myReaction: uid ? reactions[uid] || null : null,
    total: Object.keys(reactions).length,
  };
}

export async function togglePostReaction(postId, uid, emoji = "❤️") {
  if (!postId || !uid) return;

  const ref = db
    .collection("posts")
    .doc(postId)
    .collection("reactions")
    .doc(uid);

  await db.runTransaction(async (transaction) => {
    const snapshot = await transaction.get(ref);
    const previous = snapshot.exists ? snapshot.data() : null;

    if (!emoji || previous?.emoji === emoji) {
      if (snapshot.exists) transaction.delete(ref);
      return;
    }

    transaction.set(
      ref,
      {
        emoji,
        createdAt:
          previous?.createdAt ||
          firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      },
      { merge: true },
    );
  });
}

export async function bumpPostSignals(uid, post, kind) {
  if (!uid || !post?.id || !post?.authorId) return;

  const weights = {
    like: { topic: 2, tag: 1, author: 1 },
    save: { topic: 3, tag: 2, author: 1 },
    comment_open: { topic: 1, tag: 1, author: 1 },
    follow: { topic: 1, tag: 1, author: 3 },
    seen: { topic: 0, tag: 0, author: 0 },
  }[kind];

  if (!weights) return;

  const increment = firebase.firestore.FieldValue.increment;
  const payload = {
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  if (post.type && weights.topic) {
    payload[`postSignals.topicScore.${safeKey(post.type)}`] = increment(
      weights.topic,
    );
  }

  if (weights.author) {
    payload[`postSignals.authorScore.${safeKey(post.authorId)}`] = increment(
      weights.author,
    );
  }

  (post.tags || [])
    .map(safeKey)
    .filter(Boolean)
    .slice(0, 3)
    .forEach((tagKey) => {
      if (weights.tag) {
        payload[`postSignals.tagScore.${tagKey}`] = increment(weights.tag);
      }
    });

  if (kind === "seen") {
    payload[`postSignals.seen.${post.id}`] = Date.now();
  }

  await db.collection("users").doc(uid).update(payload);
  prunePostSignals(uid).catch((error) =>
    console.log("Post signals prune error:", error),
  );
}
